import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus, ScanLine, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RepairStatusBadge } from '@/components/repairs';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/form-field';
import { Card, Input, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Paginated, RepairListItem } from '@/lib/types';
import { cn, formatDate, formatRelative } from '@/lib/utils';

const TABS = [
  { value: 'open', label: 'In der Werkstatt' },
  { value: 'mine', label: 'Mir zugewiesen' },
  { value: 'ready', label: 'Abholbereit' },
  { value: 'done', label: 'Abgeschlossen' },
  { value: 'all', label: 'Alle' },
] as const;

export function RepairsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'open';
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (search) next.set('q', search);
      else next.delete('q');
      setParams(next, { replace: true });
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = new URLSearchParams({ page: String(page), pageSize: '25', state: tab === 'mine' ? 'open' : tab });
  if (tab === 'mine') query.set('technician', 'me');
  if (params.get('q')) query.set('q', params.get('q')!);
  if (params.get('customerId')) query.set('customerId', params.get('customerId')!);

  const { data, isLoading } = useQuery({
    queryKey: ['repairs', 'list', query.toString()],
    queryFn: () => api<Paginated<RepairListItem>>(`/repairs?${query}`),
    placeholderData: keepPreviousData,
  });

  /** Eingabe einer Reparaturnummer (z. B. per Barcode-Scanner) öffnet den Vorgang direkt. */
  const openByNumber = async () => {
    const n = search.trim().toUpperCase();
    if (!/^[A-Z]+-?\d+$/.test(n)) return;
    try {
      const r = await api<{ id: string }>(`/repairs/by-number/${encodeURIComponent(n)}`);
      navigate(`/repairs/${r.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) toast.error(`Reparatur ${n} nicht gefunden`);
    }
  };

  return (
    <>
      <PageHeader
        title="Reparaturen"
        description="Geräteannahme, Werkstatt und Abholung"
        actions={
          can('repairs.intake') && (
            <Button asChild>
              <Link to="/repairs/new">
                <Plus /> Gerät annehmen
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('tab', t.value);
              setParams(next, { replace: true });
              setPage(1);
            }}
            className={cn(
              'rounded-full border px-3 py-1 text-sm transition-colors',
              tab === t.value ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Card>
        <div className="border-b p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8 pr-10"
              placeholder="Reparaturnummer, Seriennummer, Modell, Kunde … (Enter öffnet eine Nummer direkt)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && openByNumber()}
              aria-label="Suche"
            />
            <ScanLine className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Reparatur</TH>
              <TH className="hidden md:table-cell">Kunde</TH>
              <TH>Status</TH>
              <TH className="hidden lg:table-cell">Techniker</TH>
              <TH className="hidden sm:table-cell">Angenommen</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={5}>
                  <Skeleton className="h-32" />
                </TD>
              </TR>
            )}
            {data?.items.length === 0 && <EmptyRow colSpan={5}>Keine Reparaturen in dieser Ansicht.</EmptyRow>}
            {data?.items.map((r) => {
              const overdue = r.estimatedCompletionAt && new Date(r.estimatedCompletionAt) < new Date() && !['READY_FOR_PICKUP', 'PICKED_UP', 'CANCELLED'].includes(r.status);
              return (
                <TR key={r.id} className="cursor-pointer" onClick={() => navigate(`/repairs/${r.id}`)}>
                  <TD className="max-w-md">
                    <span className="font-mono text-xs text-muted-foreground">{r.number}</span>
                    <p className="truncate font-medium">{r.device.label}</p>
                    <p className="truncate text-xs text-muted-foreground">{r.faultDescription}</p>
                  </TD>
                  <TD className="hidden md:table-cell">{r.customer.name}</TD>
                  <TD>
                    <RepairStatusBadge status={r.status} />
                    {r.estimatedCompletionAt && (
                      <p className={cn('mt-1 text-xs', overdue ? 'font-medium text-destructive' : 'text-muted-foreground')}>bis {formatDate(r.estimatedCompletionAt)}</p>
                    )}
                  </TD>
                  <TD className="hidden lg:table-cell">{r.technician?.name ?? <span className="text-muted-foreground">–</span>}</TD>
                  <TD className="hidden whitespace-nowrap text-muted-foreground sm:table-cell">{formatRelative(r.receivedAt)}</TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
    </>
  );
}
